import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { ArrowUpRight, CheckCheck, PlusIcon, RefreshCcwIcon, SaveIcon, Wallet } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { z } from 'zod';

import { useCreateTransferFromReceipt } from '@/api/receipt/use-create-transfer-from-receipt.ts';
import { useMarkReceiptProcessed } from '@/api/receipt/use-mark-receipt-processed.ts';
import { useReceipt } from '@/api/receipt/use-receipt.ts';
import { useReceiptTransferSuggestions } from '@/api/receipt/use-receipt-transfer-suggestions.ts';
import { useReloadReceipt } from '@/api/receipt/use-reload-receipt.ts';
import { useUpdateReceipt } from '@/api/receipt/use-update-receipt.ts';
import {
  ReceiptDtoStatusEnum,
  ReceiptItemDtoTypeEnum,
  type TransferDto,
  type UpdateReceiptDto,
} from '@/api/types';
import DrawerMobileClose from '@/components/drawer-mobile-close/drawer-mobile-close.tsx';
import FormInput from '@/components/form/form-input/form-input.tsx';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Label } from '@/components/ui/label';
import MatchingTransferRow from '@/features/receipts/matching-transfer-row/matching-transfer-row.tsx';
import ReceiptDiscountRow from '@/features/receipts/receipt-discount-row/receipt-discount-row.tsx';
import ReceiptItemRow from '@/features/receipts/receipt-item-row/receipt-item-row.tsx';
import ReceiptStatus from '@/features/receipts/receipt-status/receipt-status.tsx';
import { formatPrice } from '@/helpers/formatters.ts';

const updateReceiptSchema = z.object({
  party: z.string().min(1, 'Party is required'),
});

type ReceiptDetailDrawerProps = {
  receiptId: string;
};

const ReceiptDetailDrawer = ({ receiptId }: ReceiptDetailDrawerProps) => {
  const navigate = useNavigate();
  const { projectId } = useParams();

  const form = useForm<UpdateReceiptDto>({
    resolver: zodResolver(updateReceiptSchema),
  });

  const { data: receipt, isLoading } = useReceipt(receiptId);
  const { mutateAsync: updateReceipt, isPending: updateLoading } = useUpdateReceipt(receiptId);
  const { mutateAsync: reloadReceipt, isPending: reloadLoading } = useReloadReceipt(receiptId);
  const { mutateAsync: markProcessed, isPending: markProcessedLoading } =
    useMarkReceiptProcessed(receiptId);
  const { data: transferSuggestions } = useReceiptTransferSuggestions(
    receiptId,
    !receipt?.assignedTransfer,
  );
  const { mutateAsync: createTransferFromReceipt, isPending: createTransferLoading } =
    useCreateTransferFromReceipt(receiptId);

  useEffect(() => {
    form.reset({ party: receipt?.party || '' });
  }, [receipt?.party]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      navigate('..');
    }
  };

  const handleReload = () => {
    reloadReceipt().catch(() => undefined);
  };

  const handleMarkProcessed = () => {
    markProcessed()
      .then(() => {
        navigate('..');
      })
      .catch(() => undefined);
  };

  const onSubmit = (data: UpdateReceiptDto) => {
    updateReceipt(data).catch(() => undefined);
  };

  const handleCreateTransfer = () => {
    if (confirm('Create a new cash transfer from this receipt and assign it?')) {
      createTransferFromReceipt().catch(() => undefined);
    }
  };

  if (!receipt) {
    return (
      <Drawer direction="right" open onOpenChange={handleOpenChange}>
        <DrawerContent className="data-[vaul-drawer-direction=right]:w-full! data-[vaul-drawer-direction=right]:max-w-full! data-[vaul-drawer-direction=right]:sm:w-[800px]! data-[vaul-drawer-direction=right]:sm:max-w-[90vw]!">
          <div className="flex flex-col gap-4 p-4 overflow-y-auto">
            <div className="flex justify-end sm:hidden">
              <DrawerMobileClose />
            </div>
            {isLoading && (
              <div className="flex items-center justify-center h-48">
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            )}
            {!isLoading && (
              <div className="flex items-center justify-center h-48">
                <span className="text-sm text-muted-foreground">Receipt not found</span>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  const discounts = receipt.items.filter((item) => item.type === ReceiptItemDtoTypeEnum.Discount);
  const items = receipt.items.filter((item) => item.type === ReceiptItemDtoTypeEnum.Item);

  const getTransferScore = (transfer: TransferDto) => {
    let result = 0;

    if (transfer.party === receipt.party || transfer.rawParty === receipt.party) {
      result += 1;
    }

    if (transfer.amount === receipt.amount) {
      result += 1;
    }

    return result;
  };

  return (
    <Drawer direction="right" open onOpenChange={handleOpenChange}>
      <DrawerContent className="data-[vaul-drawer-direction=right]:w-full! data-[vaul-drawer-direction=right]:max-w-full! data-[vaul-drawer-direction=right]:sm:w-[800px]! data-[vaul-drawer-direction=right]:sm:max-w-[90vw]!">
        <div className="flex flex-col gap-4 p-4 overflow-y-auto">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-xl font-bold">Receipt Information</h2>
                <span className="text-xs text-muted-foreground break-all">{receipt.extId}</span>
              </div>
              <DrawerMobileClose />
            </div>
            <div className="flex">
              <div className="flex flex-wrap items-center gap-2">
                <ReceiptStatus status={receipt.status} />
                {receipt.status !== ReceiptDtoStatusEnum.Processed && (
                  <Button variant="outline" disabled={reloadLoading} onClick={handleReload}>
                    <RefreshCcwIcon />
                    Reload
                  </Button>
                )}
                {receipt.status === ReceiptDtoStatusEnum.Loaded && (
                  <Button
                    variant="outline"
                    disabled={markProcessedLoading}
                    onClick={handleMarkProcessed}
                  >
                    <CheckCheck />
                    Mark processed
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">
              {receipt.date && dayjs(receipt.date).format('DD.MM.YYYY HH:mm')}
            </span>
            <span className="text-lg font-semibold">{formatPrice(receipt.amount)}</span>
          </div>
          <div className="flex flex-col items-stretch gap-2 sm:items-start">
            <FormInput
              control={form.control}
              label="Party"
              name="party"
              disabled={!receipt.party}
            />
            {form.formState.isDirty && (
              <Button disabled={updateLoading} onClick={form.handleSubmit(onSubmit)}>
                <SaveIcon />
                Save
              </Button>
            )}
          </div>
          <div className="w-full border-b border-muted-foreground/40 my-2" />
          <h2 className="text-lg font-semibold">Items ({items.length})</h2>
          <div className="flex flex-col gap-1">
            <div className="hidden items-center gap-4 px-2 py-1 sm:flex">
              <div className="grow">
                <Label>Name</Label>
              </div>
              <div className="w-24">
                <Label>Quantity</Label>
              </div>
              <div className="w-24 flex justify-end">
                <Label>Price</Label>
              </div>
              <div className="w-8" />
            </div>
            {items.map((item) => (
              <ReceiptItemRow key={item.uuid} item={item} />
            ))}
          </div>
          {!!discounts.length && (
            <>
              <div className="w-full border-b border-muted-foreground/40 my-2" />
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Discounts ({discounts.length})</h2>
                <div className="mt-1 text-lg font-semibold text-right text-red-600">
                  {formatPrice(discounts.reduce((sum, item) => sum + item.amount, 0))}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <div className="hidden items-center gap-4 px-2 py-1 sm:flex">
                  <div className="grow">
                    <Label>Name</Label>
                  </div>
                  <div className="w-24 flex justify-end">
                    <Label>Price</Label>
                  </div>
                </div>
                {discounts.map((discount) => (
                  <ReceiptDiscountRow key={discount.uuid} discount={discount} items={items} />
                ))}
              </div>
            </>
          )}
          {receipt.assignedTransfer ? (
            <>
              <div className="w-full border-b border-muted-foreground/40 my-2" />
              <h2 className="text-lg font-semibold">Assigned Transfer</h2>
              <Link
                to={`/projects/${projectId}/transfers/${receipt.assignedTransfer.uuid}`}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors text-xs"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="w-8 h-8 shrink-0 bg-muted rounded-full flex items-center justify-center">
                    <Wallet className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="font-medium">
                      {receipt.assignedTransfer.party || receipt.assignedTransfer.rawParty}
                    </span>
                    <span className="text-muted-foreground">
                      {dayjs(receipt.assignedTransfer.date).format('DD.MM.YYYY')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">
                    {formatPrice(receipt.assignedTransfer.amount)}
                  </span>
                  <ArrowUpRight className="size-3.5 text-muted-foreground" />
                </div>
              </Link>
            </>
          ) : (
            <>
              <div className="w-full border-b border-muted-foreground/40 my-2" />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-semibold">Matching Transfers</h2>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={
                    createTransferLoading ||
                    !receipt.party ||
                    !receipt.date ||
                    receipt.amount == null
                  }
                  onClick={handleCreateTransfer}
                >
                  <PlusIcon />
                  Create Transfer (Cash)
                </Button>
              </div>
              {!!transferSuggestions?.length && (
                <div className="flex flex-col gap-2">
                  {transferSuggestions
                    .sort((a, b) => getTransferScore(b) - getTransferScore(a))
                    .map((transfer) => (
                      <MatchingTransferRow
                        key={transfer.uuid}
                        receipt={receipt}
                        transfer={transfer}
                      />
                    ))}
                </div>
              )}
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default ReceiptDetailDrawer;
